import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Bolsa } from '../../../types';

const PURPLE = '#93408F';
const INK = '#2E2430';
const MUTED = '#A79AA7';
const HAIR = '#F0E9F0';
const WASH = '#FAF7FA';
const RED = '#CB4444';
const FAINT = '#C9BFC9';

interface BagManageModalProps {
  open: boolean;
  bolsas: Bolsa[];
  onClose: () => void;
  // SPEC-119 (R-11): confirma la selección (checkboxes) o una papelera individual → abre BagDeleteConfirm
  onSolicitarBorrado: (ids: string[]) => void;
}

// SPEC-119 (R-11, UC-007): modal de gestión con selección múltiple y borrado en lote.
export function BagManageModal({ open, bolsas, onClose, onSolicitarBorrado }: BagManageModalProps) {
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSeleccionadas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '20px', width: 470, maxWidth: '92vw' } }}>
      <Box sx={{ padding: '28px 30px', maxHeight: '86vh', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: MUTED }}>
          GESTIONAR BOLSAS
        </Typography>
        <Typography sx={{ fontSize: 23, fontWeight: 700, color: INK, mt: '4px' }}>
          Bolsas abiertas · {bolsas.length}
        </Typography>
        <Typography sx={{ fontSize: 16, color: MUTED, mt: '6px', mb: '16px' }}>
          Selecciona para borrar en lote, o usa la papelera de cada fila.
        </Typography>

        {bolsas.map(bolsa => {
          const lineas = bolsa.lineas ?? [];
          const unidades = lineas.reduce((acc, l) => acc + l.cantidad, 0);
          const checked = seleccionadas.has(bolsa.id);
          return (
            <Box
              key={bolsa.id}
              data-testid={`manage-row-${bolsa.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                border: `1px solid ${HAIR}`,
                borderRadius: '12px',
                padding: '11px 13px',
                mb: '8px',
                '&:hover': { background: WASH },
              }}
            >
              <Checkbox
                data-testid={`manage-checkbox-${bolsa.id}`}
                checked={checked}
                onChange={() => toggle(bolsa.id)}
                sx={{ color: FAINT, '&.Mui-checked': { color: PURPLE }, p: 0 }}
              />
              <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                {lineas.slice(0, 4).map(l => (
                  <Box
                    key={l.id}
                    component={l.imagen_url ? 'img' : 'div'}
                    src={l.imagen_url ?? undefined}
                    sx={{
                      width: 24, height: 24, borderRadius: '7px', border: `1px solid ${HAIR}`,
                      objectFit: 'cover', background: '#fff', flexShrink: 0,
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: INK }}>
                  {bolsa.codigo_bolsa ?? bolsa.nombre} · {bolsa.cliente_razon_social ?? 'Sin cliente asociado'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: MUTED }}>
                  {bolsa.lineas_count} refs · {unidades.toLocaleString('es-CO')} unds · $ {bolsa.total.toLocaleString('es-CO')}
                </Typography>
              </Box>
              <IconButton
                data-testid={`manage-trash-${bolsa.id}`}
                size="small"
                onClick={() => onSolicitarBorrado([bolsa.id])}
                sx={{ color: MUTED, '&:hover': { background: 'rgba(203,68,68,.10)', color: RED } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          );
        })}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: '20px' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ borderColor: HAIR, color: INK, textTransform: 'none', borderRadius: '999px' }}
          >
            Cerrar
          </Button>
          <Button
            data-testid="btn-borrar-seleccionadas"
            disabled={seleccionadas.size === 0}
            onClick={() => onSolicitarBorrado(Array.from(seleccionadas))}
            variant="contained"
            sx={{
              background: RED,
              textTransform: 'none',
              borderRadius: '999px',
              '&:hover': { background: RED, filter: 'brightness(.9)' },
              '&.Mui-disabled': { background: FAINT, color: '#fff' },
            }}
          >
            Borrar seleccionadas ({seleccionadas.size})
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
